import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTRPC } from "@/trpc";
import { saveBlob } from "@/ajax/documents/saveBlob";
import type { CredentialVerification } from "../types";
import {
  vaultScopeKey,
  type CredentialVaultScope,
} from "../lib/credential-vault-scope";
import type { PreviewDocument } from "./use-credential-document-url";
import { vaultDownloadName } from "../lib/vault-file-downloads";

type Selection = {
  scopeKey: string;
  verification: CredentialVerification;
  url: string | null;
  error: string | null;
};

/** Signed links are minted on demand from the verification, never from an
 * arbitrary upload ID. The server authorizes its exact held credential version. */
export function useCredentialVerificationPreview(scope: CredentialVaultScope) {
  const trpc = useTRPC();
  const scopeKey = vaultScopeKey(scope);
  const [selection, setSelection] = useState<Selection | null>(null);
  useEffect(() => setSelection(null), [scopeKey]);
  const forBu = useMutation(
    trpc.credentials.getVerificationEvidenceUrlForBu.mutationOptions(),
  );
  const forSelf = useMutation(
    trpc.credentials.getMyVerificationEvidenceUrl.mutationOptions(),
  );
  const downloadEvidence = useMutation({
    mutationFn: async (verificationId: string) => {
      const result = await mintUrl({ verificationId, asAttachment: true });
      const response = await fetch(result.signedUrl, { credentials: "omit" });
      if (!response.ok) {
        throw new Error(
          "Could not download verification evidence. Please retry.",
        );
      }
      await saveBlob({
        blob: await response.blob(),
        fileName: vaultDownloadName(result.fileName),
      });
    },
    onError: (error) => toast.error(error.message),
  });
  const current = selection?.scopeKey === scopeKey ? selection : null;

  async function mintUrl(input: {
    verificationId: string;
    asAttachment: boolean;
  }) {
    if (scope.kind === "organization") {
      return forBu.mutateAsync({
        organizationBusinessUnitId: scope.organizationBusinessUnitId,
        providerUserId: scope.providerUserId,
        credentialViewId: scope.credentialViewId,
        expectedAffiliationId: scope.expectedAffiliationId,
        ...input,
      });
    }
    return forSelf.mutateAsync({ switchUserId: scope.userId, ...input });
  }

  async function open(verification: CredentialVerification) {
    if (!verification.evidence && !verification.registryReport) {
      return;
    }
    const next: Selection = { scopeKey, verification, url: null, error: null };
    setSelection(next);
    // Registry evidence is structured source data, not an image/PDF preview.
    // Its readable report was authorized with this credential's saved version.
    if (verification.registryReport) {
      return;
    }
    try {
      const result = await mintUrl({
        verificationId: verification.id,
        asAttachment: false,
      });
      // A closed/replaced preview must not be reopened by an older request.
      setSelection((active) =>
        active === next ? { ...next, url: result.signedUrl } : active,
      );
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Could not load verification evidence.";
      setSelection((active) =>
        active === next ? { ...next, error: message } : active,
      );
    }
  }

  function download() {
    if (!current?.verification.evidence || downloadEvidence.isPending) {
      return;
    }
    downloadEvidence.mutate(current.verification.id);
  }

  let document: PreviewDocument | null = null;
  if (current?.verification.evidence && !current.verification.registryReport) {
    document = {
      id: current.verification.id,
      title: `${current.verification.source} verification`,
      fileName: current.verification.evidence.fileName,
    };
  }

  return {
    document,
    registryVerification: current?.verification.registryReport
      ? current.verification
      : null,
    url: current?.url ?? null,
    error: current?.error ?? null,
    isLoading:
      !!current &&
      !current.verification.registryReport &&
      !current.url &&
      !current.error,
    isDownloading: downloadEvidence.isPending,
    open,
    download,
    close: () => setSelection(null),
  };
}
